import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Plus, Loader2, Briefcase, CheckCircle2 } from 'lucide-react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import MissionCard from '../../components/admin/MissionCard';
import MissionFilterChips from '../../components/admin/MissionFilterChips';

const MissionsPage = () => {
  const navigate = useNavigate();
  
  // === STATE ===
  const [missions, setMissions] = useState([]);
  const [allMissions, setAllMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [activeFilter, setActiveFilter] = useState('all');
  
  // Counts pour les filtres
  const [filterCounts, setFilterCounts] = useState({
    to_staff: 0,
    with_matches: 0,
    active: 0,
    urgent: 0,
    all: 0
  });

  // === FETCH DATA ===
  useEffect(() => {
    fetchMissions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (allMissions.length > 0) {
      applyFilter(activeFilter);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter, allMissions]);

  const fetchMissions = async () => {
    setLoading(true);
    try {
      // Exclude completed missions from decision flow
      const response = await api.get('/admin/missions?exclude_status=completed&limit=100');
      const data = await response.json();
      
      const missionsList = data.missions || [];
      setAllMissions(missionsList);
      
      // Calculate filter counts
      calculateFilterCounts(missionsList);
      
      // Apply current filter
      const filtered = applyFilterLogic(missionsList, activeFilter);
      setMissions(filtered);
    } catch (error) {
      console.error('Failed to fetch missions:', error);
      toast.error('Erreur lors du chargement des missions');
    } finally {
      setLoading(false);
    }
  };

  // === FILTER LOGIC ===
  const calculateFilterCounts = (missionsList) => {
    const counts = {
      all: missionsList.length,
      to_staff: missionsList.filter(m => m.status === 'open').length,
      with_matches: missionsList.filter(m => m.status === 'open' && (m.matches_count || 0) > 0).length,
      active: missionsList.filter(m => m.status === 'active').length,
      urgent: missionsList.filter(m => {
        const daysOld = m.days_old || 0;
        return m.status === 'open' && daysOld > 7;
      }).length
    };
    
    setFilterCounts(counts);
  };

  const applyFilterLogic = (missionsList, filter) => {
    switch (filter) {
      case 'to_staff':
        return missionsList.filter(m => m.status === 'open');
        
      case 'with_matches':
        return missionsList.filter(m => m.status === 'open' && (m.matches_count || 0) > 0);
        
      case 'active':
        return missionsList.filter(m => m.status === 'active');
        
      case 'urgent':
        return missionsList.filter(m => {
          const daysOld = m.days_old || 0;
          return m.status === 'open' && daysOld > 7;
        });
        
      default:
        return missionsList;
    }
  };

  const applyFilter = (filter) => {
    const filtered = applyFilterLogic(allMissions, filter);
    
    // Sort by priority
    const sorted = [...filtered].sort((a, b) => {
      // 1. Open missions first
      if (a.status === 'open' && b.status !== 'open') return -1;
      if (a.status !== 'open' && b.status === 'open') return 1;
      
      // 2. With matches first (among open)
      const aMatches = a.matches_count || 0;
      const bMatches = b.matches_count || 0;
      if (aMatches > 0 && bMatches === 0) return -1;
      if (aMatches === 0 && bMatches > 0) return 1;
      
      // 3. Older first (among open with no matches)
      const aDays = a.days_old || 0;
      const bDays = b.days_old || 0;
      return bDays - aDays;
    });
    
    setMissions(sorted);
  };

  // === ACTIONS ===
  const handleViewCandidates = useCallback((mission) => {
    navigate(`/admin/missions/${mission.id}/matches`);
  }, [navigate]);

  const handleViewDetail = useCallback((mission) => {
    navigate(`/admin/missions/${mission.id}`);
  }, [navigate]);

  // === RENDER ===
  const missionCount = missions.length;
  const openCount = filterCounts.to_staff;

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto" data-testid="missions-page">
      {/* === HEADER === */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-display font-semibold text-tp-text">
            Missions
          </h1>
          <p className="text-tp-text-light text-sm md:text-base">
            {openCount > 0 
              ? `${openCount} mission${openCount > 1 ? 's' : ''} à staffer`
              : 'Toutes les missions sont staffées'
            }
          </p>
        </div>
        <Link to="/admin/missions/new">
          <Button 
            className="bg-tp-primary hover:bg-tp-primary-dark text-white"
            data-testid="add-mission-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Nouvelle</span>
          </Button>
        </Link>
      </div>

      {/* === PROGRESS INDICATOR (for open missions) === */}
      {filterCounts.all > 0 && openCount > 0 && (
        <div className="mb-4">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>Missions staffées</span>
            <span>{filterCounts.all - openCount} / {filterCounts.all}</span>
          </div>
          <div className="bg-gray-100 rounded-full h-2 overflow-hidden">
            <div 
              className="bg-green-500 h-full transition-all duration-500"
              style={{ width: `${((filterCounts.all - openCount) / filterCounts.all) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* === FILTER CHIPS === */}
      <div className="mb-4">
        <MissionFilterChips
          activeFilter={activeFilter}
          onFilterChange={setActiveFilter}
          counts={filterCounts}
        />
      </div>

      {/* === MISSIONS LIST === */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-tp-text-light" />
        </div>
      ) : missions.length > 0 ? (
        <div className="space-y-4">
          {missions.map((mission) => (
            <MissionCard
              key={mission.id}
              mission={mission}
              onViewCandidates={() => handleViewCandidates(mission)}
              onViewDetail={() => handleViewDetail(mission)}
            />
          ))}
        </div>
      ) : (
        <Card className="p-12 text-center border-tp-border">
          {activeFilter !== 'all' ? (
            <>
              <Briefcase className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-tp-text mb-2">
                Aucune mission trouvée
              </h3>
              <p className="text-tp-text-light mb-4">
                Aucune mission ne correspond à ce filtre.
              </p>
              <Button 
                variant="outline"
                onClick={() => setActiveFilter('all')}
              >
                Voir toutes les missions
              </Button>
            </>
          ) : (
            <>
              <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-tp-text mb-2">
                Aucune mission en attente
              </h3>
              <p className="text-tp-text-light mb-4">
                Créez une nouvelle mission pour commencer.
              </p>
              <Link to="/admin/missions/new">
                <Button className="bg-tp-primary hover:bg-tp-primary-dark">
                  <Plus className="h-4 w-4 mr-2" />
                  Créer une mission
                </Button>
              </Link>
            </>
          )}
        </Card>
      )}
    </div>
  );
};

export default MissionsPage;
